import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const SUPPORT_EMAIL = "mgomessapi@gmail.com";

/**
 * Reusable Contact Support trigger.
 *
 * Renders an icon-only button (headset / lifebuoy) — the actual support
 * email is NEVER shown in the UI. Clicking still triggers `mailto:` so the
 * user's default mail app opens with the recipient pre-filled, plus a
 * silent clipboard fallback for users without a mail client. Toast feedback
 * never reveals the address.
 *
 * variants:
 *   - "footer"  → label + larger icon (use in footer column)
 *   - "header"  → small icon button (use in header)
 *   - "inline"  → text-only with icon (use inside thin bars)
 */
export default function ContactSupport({ variant = "footer", className = "", subject }) {
  const { t } = useTranslation();
  const subj = subject || "GreenBelt — support request";
  const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subj)}`;
  const label = t("support.contact");

  const onClick = () => {
    // Silent clipboard copy as a power-user fallback.
    try {
      navigator.clipboard?.writeText && navigator.clipboard.writeText(SUPPORT_EMAIL);
    } catch (err) {
      // Clipboard API can fail on non-HTTPS, locked-down browsers, or sandboxed iframes.
      // Surface it in dev so we know, but never bubble it up to the user.
      if (process.env.NODE_ENV !== "production") {
        console.warn("ContactSupport clipboard fallback failed:", err);
      }
    }
    toast.success(t("support.contact"), {
      description: t("support.toast"),
      duration: 5000,
    });
  };

  if (variant === "footer") {
    return (
      <div className={className}>
        <div className="label-tag mb-3">{t("support.section")}</div>
        <a
          href={href}
          onClick={onClick}
          aria-label={label}
          title={label}
          data-testid="contact-support-footer"
          className="inline-flex items-center gap-3 group"
        >
          <span className="w-10 h-10 border border-ink hover:bg-ink hover:text-bg transition-colors flex items-center justify-center">
            <i className="fa-solid fa-headset text-lg" aria-hidden="true"></i>
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink2 group-hover:text-ink">
            {label}
          </span>
        </a>
        <p className="text-xs text-ink2 mt-3 leading-relaxed">
          {t("support.tagline")}
        </p>
      </div>
    );
  }

  if (variant === "header") {
    return (
      <a
        href={href}
        onClick={onClick}
        aria-label={label}
        title={label}
        data-testid="contact-support-header"
        className={`inline-flex items-center justify-center w-8 h-8 border border-ink/30 hover:bg-ink hover:text-bg hover:border-ink text-ink transition-colors ${className}`}
      >
        <i className="fa-solid fa-headset text-sm" aria-hidden="true"></i>
      </a>
    );
  }

  // inline (used inside the DemoBanner — currentColor inherits the bar's palette)
  return (
    <a
      href={href}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid="contact-support-inline"
      className={`inline-flex items-center hover:opacity-100 opacity-80 ${className}`}
    >
      <i className="fa-solid fa-headset text-sm" aria-hidden="true"></i>
    </a>
  );
}
